import * as React from "react"
import { useLocation, Link } from "react-router-dom"
import { ShieldCheck } from "lucide-react"
import { useAdminView } from "../../hooks/useAdminView"
import { userNavItems, adminNavItems, type NavItem } from "../../config/sidebarNav"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../../../components/ui/sidebar"
import { Separator } from "../ui/separator"
import { ViewSwitcher } from "./ViewSwitcher"
import { NavUser } from "../../../components/nav-user"
import { useAuth } from "wasp/client/auth"

interface NavGroupProps {
  items: NavItem[]
  label?: string
}

function NavGroup({ items, label }: NavGroupProps) {
  const location = useLocation()

  return (
    <div>
      {label && (
        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {label}
        </div>
      )}
      <SidebarMenu className="space-y-1">
        {items.map(({ title, url, icon: Icon }) => {
          const active = location.pathname === url ||
                        (location.pathname.startsWith(url + '/') && url !== '/')

          return (
            <SidebarMenuItem key={url}>
              <SidebarMenuButton
                asChild
                isActive={active}
                className={active
                  ? 'bg-coral/10 text-ink border border-coral'
                  : 'text-dusk hover:bg-stone/20 border border-transparent'
                }
              >
                <Link to={url} className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span>{title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </div>
  )
}

export function UnifiedSidebar() {
  const { isAdminView, isAdmin } = useAdminView()
  const { data: user } = useAuth()

  // Prepare user object for NavUser component
  const u = user as any
  const appUser = u
    ? {
        name: String(u.firstName || u.displayName || u.email?.split("@")[0] || "User"),
        email: String(u.email || ""),
        avatar: undefined as string | undefined,
      }
    : { name: "Guest", email: "", avatar: undefined }

  return (
    <Sidebar variant="inset" collapsible="offcanvas">
      {/* Header */}
      <SidebarHeader className="h-14 border-b border-stone/60">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to={isAdminView ? "/admin/overview" : "/dashboard"} className="flex items-center gap-2">
                {isAdminView ? (
                  <>
                    <ShieldCheck className="w-5 h-5 text-warning" />
                    <div className="font-semibold text-dusk">nura Admin</div>
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 rounded bg-warning flex items-center justify-center text-white font-bold text-xs">
                      N
                    </div>
                    <div className="font-semibold text-dusk">nura</div>
                  </>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Content */}
      <SidebarContent className="p-3">
        {isAdmin && isAdminView ? (
          <>
            {/* Admin nav items first */}
            <NavGroup items={adminNavItems} />

            {/* Separator + User nav items */}
            <Separator className="my-4" />
            <NavGroup items={userNavItems} label="User" />
          </>
        ) : (
          /* User view: only user nav items */
          <NavGroup items={userNavItems} />
        )}
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        <ViewSwitcher />
        <NavUser user={appUser} />
      </SidebarFooter>
    </Sidebar>
  )
}
